/**
 * SwitchOrderPrintTechnologyDialog (BLU-1509)
 *
 * Switches every garment of one order that can still switch between DTF and
 * DTG, in place, in one call. The order can span batches and this view loads
 * only the expanded batch's garments, so the dialog promises no count before
 * the call; it says what will happen and shows the real answer after:
 * "N switched", then the garments not switched, grouped by reason.
 *
 * A retried call (the first one switched everything, its answer was lost)
 * comes back with nothing switched and nothing refused: that reads "Already
 * switched", never an empty result. A lock held elsewhere (thrown CONFLICT)
 * is not an answer: it reads "try again" inline and keeps the question open.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatOrderNumber } from '@threadheads/shared';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import type { RouterOutputs } from '@/lib/trpc';
import {
  otherPrintTechnology,
  type PrintTechnology,
} from '@/features/production-orders/utils/print-tech-routing';
import { useSwitchOrderPrintTechnology } from '../hooks/useStatusOverride';
import { hasPrintedLabel, switchRefusalKey } from '../lib/print-technology-switch';

export interface SwitchOrderPrintTechnologyTarget {
  orderId: string;
  orderName: string;
  to: PrintTechnology;
  locationId: string;
  /** The batch the order was opened from; refreshed after, with every touched batch. */
  batchId?: string | null;
  /** The garments loaded here (this batch only), to name refused garments by SKU. */
  garments: ReadonlyArray<{ id: string; sku: string; size: string | null }>;
}

interface SwitchOrderPrintTechnologyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SwitchOrderPrintTechnologyTarget;
}

type OrderSwitchResult = RouterOutputs['productionOrders']['switchOrderPrintTechnology'];

interface RefusalGroup {
  code: string;
  /** Refused garments loaded here, by SKU (and size). */
  named: string[];
  /** Refused garments not loaded here (another batch of the order). */
  more: number;
}

const NOTE_MAX = 500;

/** One group per reason, in the order the server first named it. */
function groupRefusals(
  refused: OrderSwitchResult['refused'],
  garments: SwitchOrderPrintTechnologyTarget['garments'],
): RefusalGroup[] {
  const byId = new Map(garments.map((g) => [g.id, g]));
  const groups = new Map<string, RefusalGroup>();
  for (const r of refused) {
    const group = groups.get(r.errorCode) ?? { code: r.errorCode, named: [], more: 0 };
    const garment = byId.get(r.garmentUnitId);
    if (garment) group.named.push(garment.size ? `${garment.sku} (${garment.size})` : garment.sku);
    else group.more += 1;
    groups.set(r.errorCode, group);
  }
  return [...groups.values()];
}

export function SwitchOrderPrintTechnologyDialog({ open, onOpenChange, target }: SwitchOrderPrintTechnologyDialogProps) {
  const { t } = useTranslation('production');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<OrderSwitchResult | null>(null);
  // The order was locked by another change: say try again, keep the question.
  const [busy, setBusy] = useState(false);
  const mutation = useSwitchOrderPrintTechnology({ orderId: target.orderId, batchId: target.batchId });
  const isPending = mutation.isPending;
  const from = otherPrintTechnology(target.to);
  const orderNumber = formatOrderNumber(target.orderName);

  useEffect(() => {
    if (!open) {
      setNote('');
      setResult(null);
      setBusy(false);
    }
  }, [open]);

  const refusalGroups = useMemo(
    () => (result ? groupRefusals(result.refused, target.garments) : []),
    [result, target.garments],
  );

  const handleConfirm = async () => {
    setBusy(false);
    try {
      setResult(
        await mutation.mutateAsync({
          prodOrderId: target.orderId,
          locationId: target.locationId,
          target: target.to,
          note: note.trim() || undefined,
        }),
      );
    } catch (error) {
      if (trpcErrorCode(error) === 'CONFLICT') {
        setBusy(true);
        return;
      }
      toast.error(t('tracker.printTechSwitch.order.failed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const flippedCount = result?.flipped.length ?? 0;
  const refusedCount = result?.refused.length ?? 0;
  const alreadySwitched = !!result && flippedCount === 0 && refusedCount === 0;
  const anyLabelled = !!result?.flipped.some((f) => hasPrintedLabel(f.status));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle>{t('tracker.printTechSwitch.order.title', { order: orderNumber, tech: target.to })}</DialogTitle>
          {!result && (
            <DialogDescription className="text-sm">
              {t('tracker.printTechSwitch.order.body', { tech: target.to })}
            </DialogDescription>
          )}
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('tracker.printTechSwitch.atPrinterWarning')}</span>
            </div>

            <div className="space-y-2">
              <label htmlFor="order-print-tech-switch-note" className="text-sm font-medium">
                {t('tracker.printTechSwitch.noteLabel')}
              </label>
              <Textarea
                id="order-print-tech-switch-note"
                placeholder={t('tracker.printTechSwitch.notePlaceholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-[80px] text-base"
                maxLength={NOTE_MAX}
              />
              <div className="text-right text-[11px] text-muted-foreground tabular-nums">
                {note.length}/{NOTE_MAX}
              </div>
            </div>

            {busy && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{t('tracker.printTechSwitch.order.busy')}</span>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" className="h-12" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t('tracker.printTechSwitch.cancel')}
              </Button>
              <Button className="h-12" onClick={() => void handleConfirm()} disabled={isPending}>
                {isPending
                  ? t('tracker.printTechSwitch.switching')
                  : t('tracker.printTechSwitch.order.menuItem', { tech: target.to })}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* One polite announcement of the answer; the Close button stays out of it. */}
            <div className="space-y-4" role="status" aria-live="polite">
              {alreadySwitched && (
                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/20 dark:text-blue-300">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{t('tracker.printTechSwitch.order.alreadySwitched', { tech: target.to })}</span>
                </div>
              )}

              {flippedCount > 0 && (
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-base font-semibold text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className="tabular-nums">
                      {t('tracker.printTechSwitch.order.switched', { count: flippedCount, tech: target.to })}
                    </span>
                  </p>
                  {anyLabelled && (
                    <p className="text-sm text-muted-foreground">
                      {t('tracker.printTechSwitch.order.labelsStillSay', { tech: from })}
                    </p>
                  )}
                </div>
              )}

              {refusedCount > 0 && (
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-base font-semibold text-red-700 dark:text-red-400">
                    <XCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className="tabular-nums">
                      {t('tracker.printTechSwitch.order.notSwitched', { count: refusedCount })}
                    </span>
                  </p>
                  <ul className="space-y-2">
                    {refusalGroups.map((group) => {
                      const key = switchRefusalKey(group.code);
                      return (
                        <li
                          key={group.code}
                          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300"
                        >
                          {key && <p>{t(key, { tech: target.to })}</p>}
                          {group.named.length > 0 && (
                            <p className="mt-1 min-w-0 break-all font-mono text-xs">{group.named.join(', ')}</p>
                          )}
                          {group.more > 0 && (
                            <p className="mt-1 text-xs tabular-nums">
                              {t('tracker.printTechSwitch.order.moreGarments', { count: group.more })}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button className="h-12" onClick={() => onOpenChange(false)}>
                {t('tracker.printTechSwitch.order.close')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
